import analyzeReady from './fixtures/analyze_ready.json';
import analyzeNeedsInput from './fixtures/analyze_needs_input.json';
import analyzeShortfall from './fixtures/analyze_shortfall.json';
import analyzeNoMatch from './fixtures/analyze_no_match.json';
import executeSuccess from './fixtures/execute_success.json';
import executePartial from './fixtures/execute_partial.json';
import executeConflict from './fixtures/execute_conflict.json';
import executeExpired from './fixtures/execute_expired.json';
import analyzeTransferReady from './fixtures/analyze_transfer_ready.json';
import analyzeTransferNeedsInput from './fixtures/analyze_transfer_needs_input.json';
import executeTransferSuccess from './fixtures/execute_transfer_success.json';
import analyzeCopyReady from './fixtures/analyze_copy_ready.json';
import analyzeCopyNeedsInput from './fixtures/analyze_copy_needs_input.json';
import executeCopySuccess from './fixtures/execute_copy_success.json';
import configFixture from './fixtures/config.json';
import contractsModule from './contracts';
import * as crewService from '../../../services/crew';

const contracts = contractsModule.default || contractsModule;
const {
  validateProposalResponse, validateExecuteResponse,
  validateTransferOwnerResponse, validateCopyCrewResponse, INTENT_TYPE,
} = contracts;

export const USE_MOCK = false;

let lastTestedKeyword = 'ready';
// Which intent's analyze()/execute() fixture family is active for the in-progress proposal --
// reset each analyze() call, read by clarify()/execute() to route to the right fixtures.
let activeIntentType = INTENT_TYPE.CREATE_CREW;
// Mocks a real proposal row's persisted partial progress across clarify() turns -- reset
// whenever a new analyze() starts a fresh proposal, accumulated across each clarify() call
// on it. Without this, turn 2 has no memory of what turn 1 already resolved.
let clarifyProgress = {};

const delay = (ms = 1800) => new Promise((resolve) => setTimeout(resolve, ms));
const clone = (obj) => (obj ? JSON.parse(JSON.stringify(obj)) : obj);
const rethrowApiError = (error) => {
  const body = error?.response?.data;
  if (body?.message) error.message = body.message;
  if (body?.code) error.code = body.code;
  throw error;
};

const TRANSFER_OWNER_OPTIONS = analyzeTransferNeedsInput.clarifications[0].options;

/** Client-side stand-in for the backend's intentExtractor's newOwnerName resolution: matches
 * a free-text mention of a crew lead by either their first or last name, not just a fixed
 * " to <name>" phrase shape. */
function extractOwnerFromText(text) {
  const lower = (text || '').toLowerCase();
  return TRANSFER_OWNER_OPTIONS.find((opt) => {
    const [last, first] = opt.label.toLowerCase().split(',').map((s) => s.trim());
    return (last && lower.includes(last)) || (first && lower.includes(first));
  }) || null;
}

function readyTransferFor(owner) {
  const response = clone(analyzeTransferReady);
  response.newOwnerId = owner.id;
  response.newOwnerName = owner.label;
  return response;
}

// Demo-fixed "today" per the mock's fixture dates: 2026-09-17 (Thursday).
const TARGET_DATE_PHRASES = [
  { test: /\btomorrow\b/, date: '2026-09-18', label: 'Fri 18 Sep' },
  { test: /\bfri(day)?\b/, date: '2026-09-18', label: 'Fri 18 Sep' },
  { test: /\bsat(urday)?\b/, date: '2026-09-19', label: 'Sat 19 Sep' },
  { test: /\bsun(day)?\b/, date: '2026-09-20', label: 'Sun 20 Sep' },
  { test: /\bmon(day)?\b/, date: '2026-09-21', label: 'Mon 21 Sep' },
  { test: /\btue(sday)?\b/, date: '2026-09-22', label: 'Tue 22 Sep' },
  { test: /\bwed(nesday)?\b/, date: '2026-09-23', label: 'Wed 23 Sep' },
  { test: /\bthu(rsday)?\b|\bnext week\b/, date: '2026-09-24', label: 'Thu 24 Sep' },
];

/** Client-side stand-in for the backend's timeResolver.resolveDate: turns a free-text date
 * phrase ("Friday", "tomorrow", "next Monday") into a concrete date, same free-text
 * tolerance the CREATE_CREW mock already has for event names and time ranges. */
function extractTargetDateFromText(text) {
  const lower = (text || '').toLowerCase();
  return TARGET_DATE_PHRASES.find((p) => p.test.test(lower)) || null;
}

function readyCopyFor(target) {
  const response = clone(analyzeCopyReady);
  response.targetDate = target.date;
  response.targetDateLabel = target.label;
  return response;
}

/**
 * Run analyze for crew intent.
 * Keyword routing:
 *  "shortfall" -> analyze_shortfall
 *  "clarify"   -> analyze_needs_input
 *  "nomatch"   -> analyze_no_match
 *  "partial"   -> sets mode to partial for execute, returns analyze_ready
 *  "conflict"  -> sets mode to conflict for execute, returns analyze_ready
 *  "expired"   -> sets mode to expired for execute, returns analyze_ready
 *  "transfer"  -> TRANSFER_OWNER intent; READY if a crew lead's name is found in the prompt,
 *                 else NEEDS_INPUT (same free-text extraction clarify() uses)
 *  "copy"      -> COPY_CREW intent; READY if a target date phrase is found in the prompt,
 *                 else NEEDS_INPUT
 *  otherwise   -> analyze_ready (CREATE_CREW)
 */
export async function analyze(prompt, timezone, extra = {}) {
  if (!USE_MOCK) {
    if (typeof crewService.analyzeCrewCall !== 'function') {
      throw new Error('crewService.analyzeCrewCall is not defined');
    }
    try {
      // extra = { selection } from a clicked choice, or { previous } from the last answer; the server checks both
      const res = await crewService.analyzeCrewCall({ prompt, timezone, ...(extra.selection ? { selection: extra.selection } : {}), ...(extra.previous ? { previous: extra.previous } : {}) });
      return res?.data;
    } catch (error) { return rethrowApiError(error); }
  }

  await delay(1800);
  const lower = (prompt || '').toLowerCase();
  clarifyProgress = {};
  activeIntentType = INTENT_TYPE.CREATE_CREW;

  let response;
  let validator = validateProposalResponse;
  if (lower.includes('transfer')) {
    lastTestedKeyword = 'transfer';
    activeIntentType = INTENT_TYPE.TRANSFER_OWNER;
    validator = validateTransferOwnerResponse;
    const owner = extractOwnerFromText(prompt);
    response = owner ? readyTransferFor(owner) : clone(analyzeTransferNeedsInput);
  } else if (lower.includes('copy')) {
    lastTestedKeyword = 'copy';
    activeIntentType = INTENT_TYPE.COPY_CREW;
    validator = validateCopyCrewResponse;
    const target = extractTargetDateFromText(prompt);
    response = target ? readyCopyFor(target) : clone(analyzeCopyNeedsInput);
  } else if (lower.includes('shortfall')) {
    lastTestedKeyword = 'shortfall';
    response = clone(analyzeShortfall);
  } else if (lower.includes('clarify') || lower.includes('needs_input')) {
    lastTestedKeyword = 'clarify';
    response = clone(analyzeNeedsInput);
  } else if (lower.includes('nomatch') || lower.includes('no_match')) {
    lastTestedKeyword = 'nomatch';
    response = clone(analyzeNoMatch);
  } else if (lower.includes('partial')) {
    lastTestedKeyword = 'partial';
    response = clone(analyzeReady);
  } else if (lower.includes('conflict')) {
    lastTestedKeyword = 'conflict';
    response = clone(analyzeReady);
  } else if (lower.includes('expired')) {
    lastTestedKeyword = 'expired';
    response = clone(analyzeReady);
  } else {
    lastTestedKeyword = 'ready';
    response = clone(analyzeReady);
  }

  if (process.env.NODE_ENV !== 'production' && typeof validator === 'function') {
    const violations = validator(response);
    if (violations && violations.length > 0) {
      console.warn('[contracts:validateProposalResponse violations in mockApi.analyze]:', violations);
    }
  }

  return response;
}

const MOCK_EVENT_KEYWORDS = {
  '66f0e5aa11bb22cc33dd4400': ['acme'],
  '66f0e5aa11bb22cc33dd4401': ['biomed', 'bio med', 'bio'],
  '66f0e5aa11bb22cc33dd4402': ['regional', 'auto show', 'auto'],
};

const to24h = (raw) => {
  const m = String(raw).toLowerCase().match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
  if (!m) return null;
  let hour = Number(m[1]) % 12;
  if (m[3] === 'pm') hour += 12;
  return `${String(hour).padStart(2, '0')}:${m[2] || '00'}`;
};

/** Client-side stand-in for the backend's clarifyExtractor: turns a free-text reply into
 * whatever fields it actually addresses, so USE_MOCK demos the same "still missing ->
 * ask again" multi-turn behavior as the live backend, not just a single hardcoded result. */
function extractMockClarifyAnswers(answerText, pendingClarifications) {
  const lower = (answerText || '').toLowerCase();
  const resolved = {};

  const eventCl = pendingClarifications.find((c) => c.field === 'eventId');
  if (eventCl) {
    const hit = (eventCl.options || []).find((opt) =>
      (MOCK_EVENT_KEYWORDS[opt.id] || []).some((kw) => lower.includes(kw)));
    if (hit) resolved.eventId = hit.id;
  }

  const timeRange = lower.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm))\s*(?:to|-|until)\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm))/);
  if (timeRange && pendingClarifications.some((c) => c.field === 'start')) {
    const start = to24h(timeRange[1]);
    const end = to24h(timeRange[2]);
    if (start && end) { resolved.start = start; resolved.end = end; }
  }

  return resolved;
}

function applyMockClarifyAnswers(crew, resolved, pendingClarifications) {
  if (resolved.eventId) {
    const opt = pendingClarifications.find((c) => c.field === 'eventId')?.options
      .find((o) => o.id === resolved.eventId);
    if (opt) crew.event = { id: opt.id, code: opt.id, label: opt.label };
  }
  if (resolved.start) crew.start = resolved.start;
  if (resolved.end) crew.end = resolved.end;
}

/**
 * Submit a free-text/spoken clarification reply.
 */
export async function clarify(proposalId, answerText) {
  if (!USE_MOCK) {
    if (typeof crewService.clarifyCrewCall !== 'function') {
      throw new Error('crewService.clarifyCrewCall is not defined');
    }
    try {
      const res = await crewService.clarifyCrewCall({ proposalId, ...(typeof answerText === 'object' ? { answers: answerText } : { answerText }) });
      return res?.data;
    } catch (error) { return rethrowApiError(error); }
  }

  await delay(1800);

  if (activeIntentType === INTENT_TYPE.TRANSFER_OWNER) {
    const owner = typeof answerText === 'object' ? TRANSFER_OWNER_OPTIONS.find(o => o.id === answerText.newOwnerId) : extractOwnerFromText(answerText);
    const response = owner ? readyTransferFor(owner) : clone(analyzeTransferNeedsInput);
    if (process.env.NODE_ENV !== 'production') {
      const violations = validateTransferOwnerResponse(response);
      if (violations.length > 0) console.warn('[contracts:validateTransferOwnerResponse violations in mockApi.clarify]:', violations);
    }
    return response;
  }

  if (activeIntentType === INTENT_TYPE.COPY_CREW) {
    const target = typeof answerText === 'object' && answerText.targetDate ? { date: answerText.targetDate, label: answerText.targetDate } : extractTargetDateFromText(answerText);
    const response = target ? readyCopyFor(target) : clone(analyzeCopyNeedsInput);
    if (process.env.NODE_ENV !== 'production') {
      const violations = validateCopyCrewResponse(response);
      if (violations.length > 0) console.warn('[contracts:validateCopyCrewResponse violations in mockApi.clarify]:', violations);
    }
    return response;
  }

  const allClarifications = analyzeNeedsInput.clarifications;
  Object.assign(clarifyProgress, typeof answerText === 'object' ? answerText : extractMockClarifyAnswers(answerText, allClarifications));
  const remaining = allClarifications.filter((cl) => {
    if (cl.field === 'eventId') return clarifyProgress.eventId === undefined;
    if (cl.field === 'start' || cl.field === 'end') return clarifyProgress.start === undefined;
    return true;
  });

  const response = remaining.length > 0 ? clone(analyzeNeedsInput) : clone(analyzeReady);
  if (remaining.length > 0) response.clarifications = remaining;
  if (response.crew) applyMockClarifyAnswers(response.crew, clarifyProgress, allClarifications);

  if (process.env.NODE_ENV !== 'production' && typeof validateProposalResponse === 'function') {
    const violations = validateProposalResponse(response);
    if (violations && violations.length > 0) {
      console.warn('[contracts:validateProposalResponse violations in mockApi.clarify]:', violations);
    }
  }

  return response;
}

/**
 * Execute crew creation and scheduling.
 */
export async function execute(proposalId, crewLeadId, selectedEmployeeIds) {
  if (!USE_MOCK) {
    if (typeof crewService.executeCrewCall !== 'function') {
      throw new Error('crewService.executeCrewCall is not defined');
    }
    try {
      const res = await crewService.executeCrewCall({ proposalId, crewLeadId, selectedEmployeeIds });
      return res?.data;
    } catch (error) { return rethrowApiError(error); }
  }

  await delay(1800);

  let response;
  if (activeIntentType === INTENT_TYPE.TRANSFER_OWNER) {
    response = clone(executeTransferSuccess);
  } else if (activeIntentType === INTENT_TYPE.COPY_CREW) {
    response = clone(executeCopySuccess);
  } else if (lastTestedKeyword === 'partial') {
    response = clone(executePartial);
  } else if (lastTestedKeyword === 'conflict') {
    response = clone(executeConflict);
  } else if (lastTestedKeyword === 'expired') {
    response = clone(executeExpired);
  } else {
    response = clone(executeSuccess);
  }

  if (process.env.NODE_ENV !== 'production' && typeof validateExecuteResponse === 'function') {
    const violations = validateExecuteResponse(response);
    if (violations && violations.length > 0) {
      console.warn('[contracts:validateExecuteResponse violations in mockApi.execute]:', violations);
    }
  }

  return response;
}

/**
 * Get Crew Call configuration.
 */
export async function getConfig() {
  if (!USE_MOCK) {
    if (typeof crewService.getCrewCallConfig !== 'function') {
      throw new Error('crewService.getCrewCallConfig is not defined');
    }
    const res = await crewService.getCrewCallConfig();
    return res?.data;
  }

  return clone(configFixture);
}
